import { Fragment } from "react";
import type { ParseKeys } from "i18next";
import { useTranslation } from "react-i18next";

import { IconBuildingFilled } from "@/components/_icons/IconBuildingFilled";
import { IconChevronLeft } from "@/components/_icons/IconChevronLeft";
import { IconInvisible } from "@/components/_icons/IconInvisible";
import { IconTeams } from "@/components/_icons/IconTeams";
import { IconUser } from "@/components/_icons/IconUser";
import { NavLink } from "@/components/_molecules/navigation-controls/nav-link";
import { Sidebar } from "@/components/_templates/sidebar/sidebar";
import { SidebarHeaderElement } from "@/components/_templates/sidebar/sidebar-header-element";
import { SidebarJoinTeamButton } from "@/components/_templates/sidebar/sidebar-join-team-button";
import { SidebarMainContent } from "@/components/_templates/sidebar/sidebar-main-content";
import { SidebarNavElement } from "@/components/_templates/sidebar/sidebar-nav-element";
import { SidebarTopContent } from "@/components/_templates/sidebar/sidebar-top-content";
import { FeatureFlagSidebarElement } from "@/domain/_shared/feature-flag/components/feature-flag-sidebar-element";
import { FeatureFlagToggle } from "@/domain/_shared/feature-flag/components/feature-flag-toggle";
import { FeatureFlagIDs } from "@/domain/_shared/feature-flag/models/config";
import { MunicipalPropertySidebarNavLink } from "@/domain/municipal-property/components/municipal-property-sidebar-nav-link";
import { useMunicipalPropertySections } from "@/domain/municipal-property/hooks/use-municipal-property-sections";
import { MUNICIPAL_PROPERTY_SEGMENT } from "@/domain/municipal-property/routes/municipal-property-base-path";
import { useCurrentUserContext } from "@/domain/user/hooks/use-current-user-context";
import { useWorkspaceById } from "@/domain/workspace/hooks/api/use-workspace-by-id";
import { useCanManageWorkspace } from "@/domain/workspace/hooks/use-can-manage-workspace";
import { makeSettingsUrl, makeWorkspaceSettingsUrl } from "@/domain/workspace/lib/make-workspace-url";
import { PlusIcon } from "@heroicons/react/24/solid";

import { SidebarSettingsAllTeamsNavElement } from "./components/sidebar-settings-all-teams-nav-element";

interface WorkspaceSettingsLink {
  path: string;
  labelKey: ParseKeys<"common">;
  flag?: FeatureFlagIDs;
  isDevelopmentBypassAllowed?: boolean;
}

const WORKSPACE_SETTINGS_LINK_LIST: readonly WorkspaceSettingsLink[] = [
  { path: "general", labelKey: "sidebar.general" },
  { path: "appearance", labelKey: "sidebar.appearance" },
  { path: "members", labelKey: "sidebar.members" },
  { path: "label", labelKey: "sidebar.label" },
  { path: "focus-area", labelKey: "sidebar.focusAreas" },
  { path: MUNICIPAL_PROPERTY_SEGMENT, labelKey: "navigation.municipalProperties" },
  { path: "phase", labelKey: "sidebar.phases" },
  { path: "status", labelKey: "sidebar.statuses", flag: "enable-status-settings-page" },
  { path: "priority", labelKey: "sidebar.priorities" },
  { path: "units", labelKey: "sidebar.units" },
  { path: "population", labelKey: "sidebar.population", flag: "enable-ageb-data", isDevelopmentBypassAllowed: false },
  { path: "team-management", labelKey: "sidebar.teamManagement" },
];

interface SidebarSettingsProps {
  workspace: {
    id: string;
  };
}

export const SidebarSettings = ({ ...props }: SidebarSettingsProps) => {
  const { t } = useTranslation("common");
  const { currentUser } = useCurrentUserContext();
  const { workspace } = useWorkspaceById(props.workspace);

  const { canManageWorkspace } = useCanManageWorkspace({ workspaceId: props.workspace.id });
  const { defaultSectionPath } = useMunicipalPropertySections();

  if (!workspace) {
    return <></>;
  }

  return (
    <Sidebar>
      <SidebarTopContent>
        <NavLink to="/" className="select-none text-xl">
          <span className="relative flex h-8 items-center overflow-hidden text-ellipsis rounded-sm pb-0 pl-1.5 pr-0.5 pt-0">
            <IconChevronLeft className="mr-3 h-4 w-4" />
            {t("sidebar.settings")}
          </span>
        </NavLink>
      </SidebarTopContent>

      <SidebarMainContent>
        <SidebarHeaderElement to={makeSettingsUrl({ workspace, path: "account" })}>
          <IconUser className="mr-2 h-4 w-4" />
          {t("sidebar.account")}
        </SidebarHeaderElement>
        <SidebarNavElement to={makeSettingsUrl({ workspace, path: "account/profile" })}>
          <IconInvisible className="mr-2 h-3 w-3" />
          {t("sidebar.profile")}
        </SidebarNavElement>
        <SidebarNavElement to={makeSettingsUrl({ workspace, path: "account/security" })}>
          <IconInvisible className="mr-2 h-3 w-3" />
          {t("sidebar.security")}
        </SidebarNavElement>

        <FeatureFlagToggle flag="enable-notification-settings">
          <SidebarNavElement to={makeSettingsUrl({ workspace, path: "account/notifications" })}>
            <IconInvisible className="mr-2 h-3 w-3" />
            {t("sidebar.notifications")}
          </SidebarNavElement>
        </FeatureFlagToggle>

        <FeatureFlagToggle flag="enable-feature-flag-page">
          <FeatureFlagSidebarElement workspace={workspace} />
        </FeatureFlagToggle>

        <div className="h-4 shrink-0"></div>

        {(canManageWorkspace || defaultSectionPath) && (
          <>
            <SidebarHeaderElement
              to={canManageWorkspace
                ? makeSettingsUrl({ workspace, path: "workspace" })
                : makeWorkspaceSettingsUrl({ workspace, path: MUNICIPAL_PROPERTY_SEGMENT })}
            >
              <IconBuildingFilled className="mr-2 h-4 w-4" />
              {t("sidebar.workspace")}
            </SidebarHeaderElement>
            {WORKSPACE_SETTINGS_LINK_LIST.map((settingsLink) => {
              if (settingsLink.path === MUNICIPAL_PROPERTY_SEGMENT) {
                return <MunicipalPropertySidebarNavLink key={settingsLink.path} workspace={workspace} />;
              }
              if (!canManageWorkspace) return null;
              const navElement = (
                <SidebarNavElement to={makeWorkspaceSettingsUrl({ workspace, path: settingsLink.path })}>
                  <IconInvisible className="mr-2 h-3 w-3" />
                  {t(settingsLink.labelKey)}
                </SidebarNavElement>
              );

              if (settingsLink.flag) {
                return (
                  <FeatureFlagToggle
                    key={settingsLink.path}
                    flag={settingsLink.flag}
                    isDevelopmentBypassAllowed={settingsLink.isDevelopmentBypassAllowed}
                  >
                    {navElement}
                  </FeatureFlagToggle>
                );
              }

              return <Fragment key={settingsLink.path}>{navElement}</Fragment>;
            })}
          </>
        )}
        <div className="h-4 shrink-0"></div>

        <SidebarHeaderElement to={makeWorkspaceSettingsUrl({ workspace, path: "teams" })}>
          <IconTeams className="mr-2 h-4 w-4" />
          {t("sidebar.myTeams")}
        </SidebarHeaderElement>

        {currentUser && (
          <>
            <SidebarSettingsAllTeamsNavElement workspace={workspace} user={currentUser} />

            <div className="mt-4" />

            <SidebarJoinTeamButton workspaceId={workspace.id} userId={currentUser.id} />

            <div className="mt-1" />

            {canManageWorkspace && (
              <SidebarNavElement to={makeWorkspaceSettingsUrl({ workspace, path: "new-team" })}>
                <PlusIcon className="mr-2 h-3.5 w-3.5" />
                {t("sidebar.addTeam")}
              </SidebarNavElement>
            )}
          </>
        )}
      </SidebarMainContent>
    </Sidebar>
  );
};
